import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
  EquipmentAsset,
  EquipmentStatus,
  MaterialCategory,
} from '@models/warehouse.model';
import { EquipmentStatusBadge } from '@components/atoms/EquipmentStatusBadge';
import { Loading } from '@components/atoms/Loading';
import { Tab } from '@components/atoms/Tab';
import { EmptyData } from '@components/molecules/EmptyData';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import SearchIcon from '@mui/icons-material/Search';
import ConstructionIcon from '@mui/icons-material/Construction';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BuildIcon from '@mui/icons-material/Build';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Badge color map matching CategoryCard color names
const badgeColors: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-700' },
  green: { bg: 'bg-green-100', text: 'text-green-700' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  red: { bg: 'bg-red-100', text: 'text-red-700' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-700' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
};
const defaultBadge = { bg: 'bg-gray-100', text: 'text-gray-700' };

interface EquipmentAssetsTableProps {
  equipment: EquipmentAsset[];
  categories: MaterialCategory[];
  isLoading?: boolean;
  onEdit?: (asset: EquipmentAsset) => void;
  onCheckout?: (asset: EquipmentAsset) => void;
  onReturn?: (asset: EquipmentAsset) => void;
  onMaintenance?: (asset: EquipmentAsset) => void;
  onViewDetail?: (asset: EquipmentAsset) => void;
  onViewTask?: (taskId: string) => void;
  onAddEquipment?: () => void;
}

type FilterTab = 'all' | 'available' | 'in_use' | 'reserved' | 'maintenance';

const ITEMS_PER_PAGE = 10;

export const EquipmentAssetsTable: React.FC<EquipmentAssetsTableProps> = ({
  equipment,
  categories,
  isLoading = false,
  onEdit,
  onCheckout,
  onReturn,
  onMaintenance,
  onViewDetail,
  onViewTask,
  onAddEquipment,
}) => {
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);

  // Get category name
  const getCategoryName = (categoryId?: string) => {
    if (!categoryId) return null;
    return categories.find((c) => c.categoryId === categoryId);
  };

  // Apply filters
  const filteredEquipment = useMemo(() => {
    return equipment.filter((asset) => {
      // Tab filter
      if (activeTab === 'available' && asset.status !== EquipmentStatus.AVAILABLE) return false;
      if (activeTab === 'in_use' && asset.status !== EquipmentStatus.IN_USE) return false;
      if (activeTab === 'reserved' && asset.status !== EquipmentStatus.RESERVED) return false;
      if (activeTab === 'maintenance' && asset.status !== EquipmentStatus.MAINTENANCE) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = asset.name.toLowerCase().includes(query);
        const matchesSerial = asset.serialNumber?.toLowerCase().includes(query);
        const matchesCode = asset.assetCode?.toLowerCase().includes(query);
        if (!matchesName && !matchesSerial && !matchesCode) return false;
      }

      // Category filter
      if (selectedCategoryId && asset.categoryId !== selectedCategoryId) return false;

      return true;
    });
  }, [equipment, activeTab, searchQuery, selectedCategoryId]);

  // Pagination
  const totalPages = Math.ceil(filteredEquipment.length / ITEMS_PER_PAGE);
  const paginatedEquipment = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredEquipment.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredEquipment, currentPage]);

  // Stats
  const stats = useMemo(() => {
    return {
      total: equipment.length,
      available: equipment.filter((e) => e.status === EquipmentStatus.AVAILABLE).length,
      inUse: equipment.filter((e) => e.status === EquipmentStatus.IN_USE).length,
      reserved: equipment.filter((e) => e.status === EquipmentStatus.RESERVED).length,
      maintenance: equipment.filter((e) => e.status === EquipmentStatus.MAINTENANCE).length,
    };
  }, [equipment]);

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleTabChange = (tab: FilterTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setCurrentPage(1);
  };

  // Format currency

  const formatDate = (date: string | Date) => format(new Date(date), 'MMM dd, yyyy');

  if (isLoading) {
    return <Loading size="xl" center />;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Assets</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <ConstructionIcon className="text-indigo-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Available</p>
              <p className="text-2xl font-semibold text-green-600">{stats.available}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="text-green-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">In Use</p>
              <p className="text-2xl font-semibold text-blue-600">{stats.inUse}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <EngineeringIcon className="text-blue-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Reserved</p>
              <p className="text-2xl font-semibold text-purple-600">{stats.reserved}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <BookmarkIcon className="text-purple-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Maintenance</p>
              <p className="text-2xl font-semibold text-orange-600">{stats.maintenance}</p>
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <BuildIcon className="text-orange-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <Tab
            tabs={[
              { id: 'all', name: 'All Equipment' },
              { id: 'available', name: 'Available', count: stats.available > 0 ? stats.available : undefined },
              { id: 'in_use', name: 'In Use', count: stats.inUse > 0 ? stats.inUse : undefined },
              { id: 'reserved', name: 'Reserved', count: stats.reserved > 0 ? stats.reserved : undefined },
              { id: 'maintenance', name: 'Maintenance', count: stats.maintenance > 0 ? stats.maintenance : undefined },
            ]}
            activeTab={activeTab}
            onTabChange={(tabId) => handleTabChange(tabId as FilterTab)}
            variant="underline"
          />

          <div className="flex-1" />

          {/* Search */}
          <div className="relative">
            <SearchIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              sx={{ fontSize: 18 }}
            />
            <input
              type="text"
              placeholder="Search by name or serial..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-64 border border-gray-300 rounded-md py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Category Filter */}
          <div className="w-48">
            <ReactSelect
              options={categories.map((cat) => ({ value: cat.categoryId, label: cat.name }))}
              value={
                selectedCategoryId
                  ? { value: selectedCategoryId, label: categories.find((c) => c.categoryId === selectedCategoryId)?.name || '' }
                  : null
              }
              onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                if (option && !Array.isArray(option) && 'value' in option) {
                  handleCategoryChange(option.value as string);
                } else {
                  handleCategoryChange('');
                }
              }}
              placeholder="All Categories"
              isClearable
            />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
        {filteredEquipment.length === 0 ? (
          <EmptyData
            description={
              searchQuery || selectedCategoryId || activeTab !== 'all'
                ? 'No equipment found. Try adjusting your filters.'
                : 'No equipment registered yet. Add your first equipment asset.'
            }
            textButton={!searchQuery && !selectedCategoryId && activeTab === 'all' ? 'Add Equipment' : undefined}
            onClickButton={onAddEquipment}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Equipment
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Location / Task
                    </th>
                    {activeTab === 'reserved' && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Reserved Period
                      </th>
                    )}
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedEquipment.map((asset) => {
                    const category = getCategoryName(asset.categoryId);
                    const isMaintenance = asset.status === EquipmentStatus.MAINTENANCE;

                    return (
                      <tr
                        key={asset.assetId}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          isMaintenance ? 'bg-orange-50' : ''
                        }`}
                        onClick={() => onViewDetail?.(asset)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isMaintenance ? 'bg-orange-100' : 'bg-gray-100'
                              }`}
                            >
                              <ConstructionIcon
                                className={isMaintenance ? 'text-orange-600' : 'text-gray-600'}
                                sx={{ fontSize: 16 }}
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{asset.name}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {asset.assetCode || asset.serialNumber || ''}{(asset.assetCode || asset.serialNumber) && asset.manufacturer ? ' · ' : ''}{[asset.manufacturer, asset.model].filter(Boolean).join(' ') || ''}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {category ? (
                            <span
                              className={`inline-block max-w-[120px] truncate px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                (badgeColors[category.color?.toLowerCase() || ''] || defaultBadge).bg
                              } ${
                                (badgeColors[category.color?.toLowerCase() || ''] || defaultBadge).text
                              }`}
                              title={category.name}
                            >
                              {category.name}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <EquipmentStatusBadge status={asset.status} size="sm" />
                        </td>
                        <td className="px-4 py-3">
                          {asset.status === EquipmentStatus.IN_USE && asset.currentTask ? (
                            <div>
                              <button type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (asset.currentTask?.taskId) {
                                    onViewTask?.(asset.currentTask.taskId);
                                  }
                                }}
                                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline text-left truncate max-w-[180px] block"
                                title={asset.currentTask.name}
                              >
                                {asset.currentTask.name}
                              </button>
                              <p className="text-xs text-gray-500 truncate">
                                {asset.currentTask.project?.name || asset.warehouse?.name || ''}
                              </p>
                            </div>
                          ) : asset.status === EquipmentStatus.RESERVED && asset.reservations && asset.reservations.length > 0 ? (
                            <div>
                              <button type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const taskId = asset.reservations?.[0]?.taskId;
                                  if (taskId) {
                                    onViewTask?.(taskId);
                                  }
                                }}
                                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline text-left truncate max-w-[180px] block"
                                title={asset.reservations[0].task?.name}
                              >
                                {asset.reservations[0].task?.name || 'Reserved'}
                              </button>
                              <p className="text-xs text-gray-500 truncate">
                                {asset.reservations[0].task?.project?.name || asset.warehouse?.name || ''}
                                {asset.reservations.length > 1 && ` (+${asset.reservations.length - 1} more)`}
                              </p>
                            </div>
                          ) : asset.status === EquipmentStatus.IN_USE || asset.status === EquipmentStatus.RESERVED ? (
                            <div>
                              <p className="text-sm text-gray-900">
                                {asset.currentTaskId ? 'Assigned to task' : 'In use'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {asset.warehouse?.name || ''}
                              </p>
                            </div>
                          ) : asset.status === EquipmentStatus.MAINTENANCE ? (
                            <div>
                              <p className="text-sm text-orange-700">In maintenance</p>
                              <p className="text-xs text-gray-500 truncate">{asset.maintenanceNotes || ''}</p>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">
                              {asset.warehouse?.name || '-'}
                            </span>
                          )}
                        </td>
                        {activeTab === 'reserved' && (
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {asset.reservations && asset.reservations.length > 0 ? (
                              <div>
                                <span>
                                  {formatDate(asset.reservations[0].startDate)} — {formatDate(asset.reservations[0].endDate)}
                                </span>
                                {asset.reservations.length > 1 && (
                                  <span className="ml-1 text-xs text-gray-500">
                                    +{asset.reservations.length - 1} more
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">No dates set</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {asset.status === EquipmentStatus.AVAILABLE && (
                              <button type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCheckout?.(asset);
                                }}
                                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                title="Checkout"
                              >
                                <LogoutIcon sx={{ fontSize: 18 }} />
                              </button>
                            )}
                            {asset.status === EquipmentStatus.IN_USE && (
                              <button type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onReturn?.(asset);
                                }}
                                className="p-1 text-green-600 hover:bg-green-100 rounded"
                                title="Return"
                              >
                                <LoginIcon sx={{ fontSize: 18 }} />
                              </button>
                            )}
                            {(asset.status === EquipmentStatus.AVAILABLE ||
                              asset.status === EquipmentStatus.RESERVED ||
                              asset.status === EquipmentStatus.IN_USE) && (
                              <button type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onMaintenance?.(asset);
                                }}
                                className="p-1 text-orange-600 hover:bg-orange-100 rounded"
                                title="Set Maintenance"
                              >
                                <BuildIcon sx={{ fontSize: 18 }} />
                              </button>
                            )}
                            <button type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.(asset);
                              }}
                              className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                              title="Edit"
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing{' '}
                  <span className="font-medium text-gray-900">
                    {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium text-gray-900">
                    {Math.min(currentPage * ITEMS_PER_PAGE, filteredEquipment.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium text-gray-900">{filteredEquipment.length}</span>{' '}
                  equipment
                </div>
                <div className="flex items-center gap-2">
                  <button type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button type="button"
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                          currentPage === pageNum
                            ? 'bg-indigo-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  {totalPages > 5 && (
                    <>
                      <span className="text-gray-500">...</span>
                      <button type="button"
                        onClick={() => handlePageChange(totalPages)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                          currentPage === totalPages
                            ? 'bg-indigo-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                  <button type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EquipmentAssetsTable;
